import { API, BRAND } from '@crowdset/contracts/constants';
import { Avatar } from '@crowdset/ui';
import { CaretDown, Flag, Queue, SignOut, Sparkle, User, Waveform } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { DropdownMenu } from 'radix-ui';
import { useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { usePrefs } from '../lib/prefs';
import { qk, useSession } from '../lib/queries';
import { Badge, Button, cx } from '../ui/kit';
import { Toasts } from './Toasts';

export function Logo({ className }: { className?: string }) {
  return (
    <span className={cx('inline-flex items-center gap-2', className)}>
      <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true">
        <rect width="32" height="32" rx="9" fill="#1d1d23" />
        <circle cx="16" cy="16" r="9" fill="none" stroke="#5fb7c9" strokeWidth="2.5" />
        <circle cx="16" cy="16" r="2.6" fill="#f0a93b" />
      </svg>
      <span className="text-[15px] font-semibold tracking-tight">{BRAND.name}</span>
    </span>
  );
}

export function DemoLabel() {
  const s = useSession().data;
  if (!s?.demo) return null;
  const parts = [
    s.authMode === 'local' ? 'seeded identities' : null,
    s.mediaMode === 'fake' ? 'test media' : null,
  ].filter(Boolean);
  return (
    <Badge tone="warn" className="whitespace-nowrap">
      <Sparkle size={12} weight="bold" aria-hidden />
      Demo<span className="hidden sm:inline">{parts.length ? ` · ${parts.join(' & ')}` : ''}</span>
    </Badge>
  );
}

function UserMenu() {
  const session = useSession().data;
  const qc = useQueryClient();
  const nav = useNavigate();
  const lowMotion = usePrefs((p) => p.lowMotion);
  const setLowMotion = usePrefs((p) => p.setLowMotion);
  const loc = useLocation();

  if (!session) return <div className="h-9 w-24 rounded-xl cs-skeleton" aria-hidden />;
  if (!session.user) {
    return (
      <Button
        variant="primary"
        size="sm"
        onClick={() => nav(`/signin?returnTo=${encodeURIComponent(loc.pathname)}`)}
      >
        Sign in
      </Button>
    );
  }
  const me = session.user;
  const signOut = async () => {
    try {
      const r = await api.post<{ redirectTo?: string }>(API.authLogout);
      await qc.invalidateQueries({ queryKey: qk.session });
      if (r?.redirectTo && r.redirectTo.startsWith('http')) window.location.assign(r.redirectTo);
      else nav('/');
    } catch {
      await qc.invalidateQueries({ queryKey: qk.session });
    }
  };
  const item =
    'flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm text-fg-muted outline-none data-[highlighted]:bg-white/[0.07] data-[highlighted]:text-fg';
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="flex h-10 items-center gap-2 rounded-xl border border-white/[0.08] bg-ink-850 pl-1 pr-2.5 text-sm hover:bg-ink-800"
          aria-label={`Account menu for ${me.displayName}`}
        >
          <span className="flex h-8 w-8 items-end justify-center overflow-hidden rounded-lg bg-ink-700">
            <Avatar avatarId={me.avatarId} size={30} state="idle" className="translate-y-2" />
          </span>
          <span className="hidden max-w-32 truncate font-medium sm:inline">{me.displayName}</span>
          <CaretDown size={14} weight="bold" className="text-fg-subtle" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 min-w-56 rounded-2xl border border-white/10 bg-ink-850 p-1.5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)]"
        >
          <div className="px-2.5 py-2">
            <div className="text-sm font-medium">{me.displayName}</div>
            <div className="font-mono text-xs text-fg-subtle">@{me.handle}</div>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-white/[0.07]" />
          <DropdownMenu.Item className={item} onSelect={() => nav('/profile')}>
            <User size={16} aria-hidden /> Profile
          </DropdownMenu.Item>
          <DropdownMenu.Item className={item} onSelect={() => nav('/library')}>
            <Queue size={16} aria-hidden /> Library
          </DropdownMenu.Item>
          {me.isPlatformAdmin && (
            <DropdownMenu.Item className={item} onSelect={() => nav('/admin')}>
              <Flag size={16} aria-hidden /> Reports (admin)
            </DropdownMenu.Item>
          )}
          <DropdownMenu.CheckboxItem
            className={item}
            checked={lowMotion}
            onCheckedChange={(v) => setLowMotion(v === true)}
          >
            <Waveform size={16} aria-hidden /> Reduce animation
            <span className="ml-auto text-xs text-fg-subtle">{lowMotion ? 'On' : 'Off'}</span>
          </DropdownMenu.CheckboxItem>
          <DropdownMenu.Separator className="my-1 h-px bg-white/[0.07]" />
          <DropdownMenu.Item className={item} onSelect={() => void signOut()}>
            <SignOut size={16} aria-hidden /> Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function Shell() {
  const lowMotion = usePrefs((p) => p.lowMotion);
  const session = useSession();
  const loc = useLocation();
  const inRoom = loc.pathname.startsWith('/r/');

  useEffect(() => {
    document.documentElement.classList.toggle('cs-low-motion', lowMotion);
  }, [lowMotion]);

  const navCls = ({ isActive }: { isActive: boolean }) =>
    cx(
      'rounded-lg px-3 py-2 text-sm transition-colors',
      isActive ? 'bg-white/[0.07] text-fg' : 'text-fg-muted hover:text-fg',
    );

  return (
    <div className="cs-grain flex min-h-[100dvh] flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-lg focus:bg-accent focus:px-3 focus:py-2 focus:text-accent-ink"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-ink-950/85 backdrop-blur-md">
        <div
          className={cx(
            'mx-auto flex h-14 items-center gap-3 px-4',
            inRoom ? 'max-w-none' : 'max-w-[1400px]',
          )}
        >
          <Link to="/" className="rounded-lg" aria-label={`${BRAND.name} home`}>
            <Logo />
          </Link>
          <nav aria-label="Primary" className="ml-2 hidden items-center gap-1 md:flex">
            <NavLink to="/" end className={navCls}>
              Rooms
            </NavLink>
            {session.data?.user && (
              <NavLink to="/library" className={navCls}>
                Library
              </NavLink>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <DemoLabel />
            <UserMenu />
          </div>
        </div>
      </header>
      <main id="main" className="flex flex-1 flex-col">
        {session.isError ? (
          <div className="mx-auto max-w-md p-10 text-sm text-fg-muted">
            CrowdSet could not start a session. The server may be unavailable.{' '}
            <button className="text-accent underline" onClick={() => void session.refetch()}>
              Try again
            </button>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      {!inRoom && (
        <footer className="border-t border-white/[0.06]">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-5 gap-y-2 px-4 py-6 text-xs text-fg-subtle">
            <span>
              {BRAND.name} is an independent project inspired by social DJ rooms. Not affiliated
              with plug.dj or YouTube.
            </span>
            <span className="ml-auto flex gap-4">
              <Link className="hover:text-fg" to="/terms">
                Terms
              </Link>
              <Link className="hover:text-fg" to="/privacy">
                Privacy
              </Link>
              <Link className="hover:text-fg" to="/youtube">
                YouTube disclosure
              </Link>
            </span>
          </div>
        </footer>
      )}
      <Toasts />
    </div>
  );
}
